import { init, reactErrorHandler } from "@sentry/react";
import "driver.js/dist/driver.css";
import { posthog } from "posthog-js";
import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "./components/custom/error-boundary";
import { RootLoader } from "./components/custom/root-loader";
import { ThemeProvider } from "./components/custom/theme/theme-provider";
import "./index.css";
import { env } from "@monorepo/env/dashboard";
import { POSTHOG_CONFIG } from "./analytics/posthog-config";
import Providers from "./providers";

// Sentry configuration constants
const DEFAULT_TRACE_SAMPLE_RATE = 0.01; // 1% default trace sample rate

// Initialize Sentry only in staging or production
if (env.VITE_SENTRY_DSN) {
  init({
    dsn: env.VITE_SENTRY_DSN,
    debug: env.VITE_DEBUG,
    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
    sendDefaultPii: true,
    tracesSampleRate: env.VITE_SENTRY_TRACES_SAMPLE_RATE
      ? Number(env.VITE_SENTRY_TRACES_SAMPLE_RATE)
      : DEFAULT_TRACE_SAMPLE_RATE,
    integrations: [],
    beforeSend(event) {
      const sessionUrl = posthog.get_session_replay_url({
        withTimestamp: true,
      });
      if (sessionUrl) {
        event.tags = { ...event.tags, posthog_session_url: sessionUrl };
      }
      return event;
    },
  });
} else if (env.VITE_DEBUG) {
  // No DSN with VITE_DEBUG=true — log Sentry events to the console so errors surface locally
  init({ debug: true });
}

/* PostHog analytics - only initialize if key is provided */
if (env.VITE_PUBLIC_POSTHOG_KEY) {
  posthog.init(env.VITE_PUBLIC_POSTHOG_KEY, POSTHOG_CONFIG);
}

const rootElement = document.getElementById("root") as HTMLElement;
// Remove the initial loader specifically
const initialLoader = document.getElementById("initial-loader");
if (initialLoader) {
  initialLoader.remove();
}
createRoot(rootElement, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: reactErrorHandler((_error, _errorInfo) => {
    // Error is handled by Sentry, no additional action needed
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: reactErrorHandler(),
}).render(
  <StrictMode>
    <ErrorBoundary>
      <ThemeProvider defaultTheme="system" storageKey="arcade-admin-theme">
        <Suspense fallback={<RootLoader />}>
          <Providers />
        </Suspense>
      </ThemeProvider>
    </ErrorBoundary>
  </StrictMode>
);
